import { computed, makeObservable, observable, action, runInAction, ObservableSet } from "mobx";
import { ABCStore } from "@noli/business";
import {
  checkAlbum,
  createAlbum,
  deleteAlbum,
  deleteAlbumPic,
  fetchAlbumDetail,
  fetchAlbumList,
  getPromptDetail,
  movePicsBetweenAlbums,
  postAddAlbum,
  PostAddAlbumType,
  renameAlbum,
  thumbsUp,
} from "./api";
import { OpMenuItem } from "./type";
import { PageLoadManager } from "@/utils/tool";

const PAGE_SIZE = 20;
export interface IWithRes<T> {
  code: number;
  errCord: number;
  data: T;
  msg: string;
}

export interface IAlbumStore {
  albumList: IAlbumItem[];
  detail: Pick<IAlbumDetail, "album_image" | "album_id">;
}

export interface IAlbumItem {
  id: number;
  image_count: number;
  is_default: number;
  name: string;
  preview: string[];
}
export interface IAlbumListRes {
  albums: IAlbumItem[];
  page_index: number;
  page_size: number;
  total: number;
}

export interface IAlbumDetail {
  album_image: IAlbumImageInfo[];
  album_id: number;
  page_index: number;
  page_size: number;
  total: number;
}

export interface IAlbumImageInfo {
  id: number;
  image_url: string;
  /**
   * 已发布的作品中的图片 id
   */
  image_id?: number;
  board_alias?: string;
  likes: number;
  is_like: boolean;
  username: string;
  user_avatar: string;
  user_id: number;
  meta_json: string;
  is_ai: number;
  width: number;
  height: number;
  created_at: number;
  /**
   * `finished` 已发布的作品
   * `board` 从画板收藏来的
   */
  source: string;
}

export interface ImgAndPromptItemType {
  album_name: string;
  album_image_id: number;
  album_id: number;
  is_album: boolean;
  opus_id: number;
  image_id: number;
  image_url: string;
  meta_json: string;
  opus_title: string;
  opus_describes?: string;
  user: {
    nickname: string;
    avatar: string;
    user_id: string;
    is_follow: boolean;
  };
  likes?: number;
  is_like?: boolean;
  is_open?: boolean;
  is_favorites?: boolean;
  status?: 0 | 1 | 2 | 3 | 4; // 0 作品正常  1 举报下架  2 待审核  3 审核未通过  4 用户删除
}

function getDefaultDetail(): Pick<IAlbumDetail, "album_image" | "album_id"> {
  return {
    album_id: 0,
    album_image: [],
  };
}
export class AlbumStore extends ABCStore<IAlbumStore> implements IAlbumStore {
  albumManager: PageLoadManager<IAlbumItem[]>;
  imagesManager: PageLoadManager<IAlbumImageInfo[]>;
  curAlbumId = 0;
  albumList: IAlbumItem[] = [];
  detail: Pick<IAlbumDetail, "album_image" | "album_id"> = getDefaultDetail();
  imageBatchOpSet: ObservableSet<number> = new ObservableSet();
  curSelectAlbum: Pick<IAlbumItem, "id" | "name"> | null = null;
  imgAndPromptList: ImgAndPromptItemType[] = [];

  constructor() {
    super();
    this.albumManager = new PageLoadManager(this.fetchAlbumHandler, PAGE_SIZE);
    this.imagesManager = new PageLoadManager(this.fetchImagesHandler, PAGE_SIZE);
    makeObservable(this, {
      albumList: observable,
      detail: observable,
      displayImages: computed,
      curAlbumId: observable,
      albumManager: observable,
      imagesManager: observable,
      imgAndPromptList: observable,
      setImgAndPromptList: action,
      clearImageSet: action,
      fetchAlbums: action,
      addAlbum: action,
      putAlbum: action,
      deleteAlbum: action,
      getAlbumDetail: action,
      movePictures: action,
      deletePictures: action,
      onThumbsUp: action,
      curSelectAlbum: observable,
      setCurSelectAlbum: action,
      clearCurSelectAlbum: action,
    });
  }

  get info(): IAlbumStore {
    return this;
  }

  get displayImages() {
    return this.detail.album_image;
  }

  get displayAlbumInfo() {
    return this.albumList.find((album) => album.id === this.curAlbumId);
  }

  get albumListOptions() {
    return this.albumList.map<OpMenuItem>((album) => {
      return {
        key: album.id + "",
        content: album.name,
      };
    });
  }

  get albumsPageIdx() {
    return this.albumManager.pageIdx;
  }

  get albumsMaxPage() {
    return this.albumManager.maxPage;
  }

  get detailPageIdx() {
    return this.imagesManager.pageIdx;
  }

  get detailMaxPages() {
    console.log("this.imagesManager.maxPage: ", this.imagesManager.maxPage);
    return this.imagesManager.maxPage;
  }

  setImgAndPromptList = (imgAndPromptList: ImgAndPromptItemType[]) => {
    this.imgAndPromptList = imgAndPromptList;
  };

  clearImageSet() {
    this.imageBatchOpSet.clear();
  }

  resetDetail() {
    this.detail = getDefaultDetail();
    this.imagesManager.reset();
    this.clearImageSet();
  }

  setCurSelectAlbum = (album: Pick<IAlbumItem, "id" | "name">) => {
    this.curSelectAlbum = album;
  };

  clearCurSelectAlbum = () => {
    this.curSelectAlbum = null;
  };

  fetchAlbumHandler = async (idx: number, size: number) => {
    const res: IWithRes<IAlbumListRes> = await fetchAlbumList({
      page_index: idx,
      page_size: size,
    });
    if (res.code === 0) {
      return {
        total: res.data.total,
        data: res.data.albums,
      };
    }
    return {
      total: 0,
      data: [],
    };
  };

  fetchImagesHandler = async (idx: number, size: number) => {
    if (!this.curAlbumId) {
      return {
        total: 0,
        data: [],
      };
    }
    const res: IWithRes<IAlbumDetail> = await fetchAlbumDetail({
      id: this.curAlbumId,
      page_index: idx,
      page_size: size,
    });
    if (res.code === 0) {
      return {
        total: res.data.total,
        data: res.data.album_image,
      };
    } else {
      return {
        total: 0,
        data: [],
      };
    }
  };

  fetchAlbums = async (increase = false) => {
    const handler = increase ? this.albumManager.next : this.albumManager.fetch;
    this.albumList = await handler();
  };

  refreshImages = async () => {
    const images = await this.imagesManager.refreshAll();
    this.detail = { album_id: this.curAlbumId, album_image: images };
  };

  refreshAlbum = async () => {
    this.albumList = await this.albumManager.refreshAll();
  };

  refreshAll = async () => {
    this.refreshAlbum();
    this.refreshImages();
  };

  addAlbum = async (name: string) => {
    const res: IWithRes<null> = await createAlbum({ name });
    if (res.code === 0) {
      this.albumList = await this.albumManager.refreshAll();
    }
  };

  putAlbum = async (payload: { id: number; name: string }) => {
    const res: IWithRes<null> = await renameAlbum(payload);
    if (res.code === 0) {
      this.albumList = await this.albumManager.refreshAll();
    }
  };

  deleteAlbum = async (payload: { id: number; type: 1 | 2 }) => {
    const res: IWithRes<null> = await deleteAlbum(payload);
    if (res.code === 0) {
      this.albumList = await this.albumManager.refreshAll();
    }
    console.log("delete res", res);
  };

  getAlbumDetail = async (id: number, increase?: boolean) => {
    runInAction(async () => {
      this.curAlbumId = id;
      const handler = increase ? this.imagesManager.next : this.imagesManager.fetch;
      const data = await handler();
      this.detail = { album_id: id, album_image: data };
    });
  };

  clearAlbumDetail = async () => {
    this.imagesManager.reset();
    this.detail = getDefaultDetail();
  };

  updateAlbums = () => {
    runInAction(async () => {
      // 更新
      this.albumList = await this.albumManager.refreshAll();
      const imagesData = await this.imagesManager.fetch();
      this.detail = { album_id: this.curAlbumId, album_image: imagesData };
      this.clearImageSet();
    });
  };

  updateAlbumDetails = () => {
    runInAction(async () => {
      // 更新
      this.albumList = await this.albumManager.refreshAll();
      const imagesData = await this.imagesManager.refreshAll();
      this.detail = { album_id: this.curAlbumId, album_image: imagesData };
      this.clearImageSet();
    });
  };

  movePictures = async (fromId: number, toId: number, pics: number[]) => {
    const res: IWithRes<null> = await movePicsBetweenAlbums({
      album_id: fromId,
      to_album_id: toId,
      album_image_ids: pics,
    });
    if (res.code === 0) {
      this.updateAlbumDetails();
    }
    return res;
  };

  deletePictures = async (albumId: number, picIds: number[]) => {
    const res: IWithRes<null> = await deleteAlbumPic({
      album_id: albumId,
      album_image_ids: picIds,
    });
    if (res.code === 0) {
      this.updateAlbumDetails();
    }
    return res;
  };

  addPictures = async (images: PostAddAlbumType[]) => {
    const res: IWithRes<null> = await postAddAlbum(images);
    if (res.code === 0) {
      this.updateAlbumDetails();
    }
    return res;
  };

  onThumbsUp = async (imgId: number | undefined) => {
    if (!imgId) return;
    const imgIdx = this.displayImages.findIndex((img) => img.image_id === imgId);
    const needCancel = this.displayImages[imgIdx].is_like;
    const res: IWithRes<null> = await thumbsUp({ image_id: imgId }, needCancel);
    if (res.code === 0) {
      const originData = this.detail!.album_image[imgIdx];
      runInAction(() => {
        this.detail!.album_image[imgIdx] = {
          ...originData,
          is_like: !needCancel,
          likes: originData.likes + (needCancel ? -1 : 1),
        };
      });
    }
  };

  fetchPromptDetail = async (imageId: number) => {
    const res: IWithRes<{ list: ImgAndPromptItemType[] }> = await getPromptDetail({
      image_id: imageId,
    });
    if (res.code === 0) {
      this.setImgAndPromptList(res.data.list);
    }
  };
}

export const albumStore = new AlbumStore();
